import { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useNotifications } from "@/context/NotificationContext";
import { Bell, ChatText, UsersThree, Check, Trash, X, Phone, CalendarBlank, PhoneSlash } from "@phosphor-icons/react";

function formatRelative(iso) {
  if (!iso) return "";
  try {
    const t = new Date(iso).getTime();
    const diff = Date.now() - t;
    if (diff < 60_000) return "just now";
    if (diff < 3_600_000) return `${Math.floor(diff / 60_000)}m ago`;
    if (diff < 86_400_000) return `${Math.floor(diff / 3_600_000)}h ago`;
    return `${Math.floor(diff / 86_400_000)}d ago`;
  } catch { return ""; }
}

export default function NotificationBell() {
  const { recent, markAllRead, clearAll, unreadTotal, smsUnread } = useNotifications();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const navigate = useNavigate();

  // Bell badge = unread entries actually visible in this session's feed.
  // (Server-side baseline counts surface via the sidebar + Messages tab badges.)
  const unreadInFeed = recent.filter((n) => !n.read).length;
  const total = unreadInFeed;

  useEffect(() => {
    const onDocClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    if (open) {
      document.addEventListener("mousedown", onDocClick);
      return () => document.removeEventListener("mousedown", onDocClick);
    }
  }, [open]);

  const openNotification = (n) => {
    setOpen(false);
    markAllRead();
    if (n.href) navigate(n.href);
  };

  return (
    <div ref={ref} className="fixed top-4 right-6 z-40" data-testid="notification-bell-wrap">
      <button
        onClick={() => setOpen((v) => !v)}
        className="relative w-10 h-10 rounded-full bg-white border border-[var(--border)] hover:bg-[var(--bg)] flex items-center justify-center shadow-sm transition-colors"
        title="Notifications"
        data-testid="notification-bell"
        aria-label="Notifications"
      >
        <Bell size={18} weight={total > 0 ? "fill" : "regular"} />
        {total > 0 && (
          <span
            className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center"
            data-testid="notification-bell-count"
          >
            {total > 99 ? "99+" : total}
          </span>
        )}
      </button>

      {open && (
        <div
          className="absolute top-12 right-0 w-[380px] max-h-[520px] bg-white border border-[var(--border)] shadow-2xl flex flex-col"
          data-testid="notification-dropdown"
        >
          <div className="flex items-center justify-between px-4 py-3 border-b border-[var(--border)]">
            <div>
              <div className="font-display font-extrabold text-[15px]">Notifications</div>
              <div className="text-[11px] text-[var(--text-muted)] font-mono tracking-wider mt-0.5">
                {unreadTotal} TEAM · {smsUnread} SMS
              </div>
            </div>
            <div className="flex items-center gap-1">
              {recent.some((n) => !n.read) && (
                <button
                  onClick={markAllRead}
                  className="p-1.5 hover:bg-[var(--bg)] text-[var(--text-muted)] hover:text-[var(--text-primary)]"
                  title="Mark all read"
                  data-testid="notif-mark-all-read"
                >
                  <Check size={14} />
                </button>
              )}
              {recent.length > 0 && (
                <button
                  onClick={clearAll}
                  className="p-1.5 hover:bg-[var(--bg)] text-[var(--text-muted)] hover:text-red-500"
                  title="Clear all"
                  data-testid="notif-clear-all"
                >
                  <Trash size={14} />
                </button>
              )}
              <button
                onClick={() => setOpen(false)}
                className="p-1.5 hover:bg-[var(--bg)]"
                title="Close"
                data-testid="notif-close"
              >
                <X size={14} />
              </button>
            </div>
          </div>

          <div className="flex-1 overflow-y-auto">
            {recent.length === 0 && (
              <div className="p-8 text-center text-[13px] text-[var(--text-muted)]">
                <Bell size={28} className="mx-auto opacity-30 mb-2" />
                <div>You&rsquo;re all caught up.</div>
                <div className="text-[11px] mt-1">New mentions, messages and SMS will appear here.</div>
              </div>
            )}
            {recent.map((n) => {
              const isMissed = (n.title || "").toLowerCase().startsWith("missed call");
              let Icon = UsersThree;
              let chipClass = "bg-indigo-100 text-indigo-700";
              if (n.kind === "sms") { Icon = ChatText; chipClass = "bg-emerald-100 text-emerald-700"; }
              else if (n.kind === "call") {
                Icon = isMissed ? PhoneSlash : Phone;
                chipClass = isMissed ? "bg-red-100 text-red-700" : "bg-blue-100 text-blue-700";
              } else if (n.kind === "meeting") {
                Icon = CalendarBlank;
                chipClass = "bg-amber-100 text-amber-700";
              } else if (n.isMention) {
                chipClass = "bg-orange-100 text-orange-700";
              }
              return (
                <button
                  key={n.id}
                  onClick={() => openNotification(n)}
                  className={`w-full text-left px-4 py-3 border-b border-[var(--border)] hover:bg-[var(--bg)] flex gap-3 transition-colors ${n.read ? "opacity-60" : ""}`}
                  data-testid={`notif-item-${n.id}`}
                >
                  <div className={`w-8 h-8 shrink-0 rounded-full flex items-center justify-center ${chipClass}`}>
                    <Icon size={14} weight="fill" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-baseline justify-between gap-2">
                      <div className={`text-[13px] truncate ${n.read ? "" : "font-semibold"}`}>{n.title}</div>
                      <div className="text-[10px] font-mono text-[var(--text-muted)] shrink-0">{formatRelative(n.time)}</div>
                    </div>
                    <div className="text-[12px] text-[var(--text-muted)] mt-0.5 line-clamp-2">{n.body}</div>
                  </div>
                  {!n.read && <div className="w-2 h-2 rounded-full bg-[var(--accent)] mt-1.5 shrink-0" />}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
